// src/App.js
import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { QueueProvider } from "./context/QueueContext";
import ProtectedRoute from "./components/ProtectedRoute";
import MentorRoute from "./components/MentorRoute";
import AccountTypeRouter from "./components/AccountTypeRouter";
import RouteTransition from "./components/RouteTransition";

// Page Imports
import HomePageNew from "./pages/HomePageNew";
import Login from "./components/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ParentConsentApproval from "./pages/ParentConsentApproval";
import FirebaseActionHandler from "./components/FirebaseActionHandler";
import CreateBooksPage from "./pages/CreateBooksPage";
import GrowPage from "./pages/GrowPage";
import ContactPage from "./pages/ContactPage";
import OfferingsPage from "./pages/OfferingsPage";
// import GalleryPage from "./pages/GalleryPage";
import GalleryStudio from "./pages/GalleryStudio";
import GeneralExhibit from "./pages/GeneralExhibit";
import UserDashboard from "./pages/UserDashboard";
import ArtistProfile from "./pages/ArtistProfile";
import ArtistWall from "./pages/ArtistWall";
import ArtistsProfiles from "./pages/ArtistsProfiles";
import ArtistPublicGallery from "./pages/ArtistPublicGallery";
import AIMagicPage from "./pages/AIMagicPage"; // ✅ AI Learning Space Magic Page
import Story from "./pages/Story"; // ✅ Story Page
import StoryCreator from "./pages/StoryCreator"; // ✅ Story Creator Page
import BookView from "./pages/BookView";
import EditBookView from "./pages/EditBookView";
import MyArtStories from "./pages/MyArtStories";
import Membership from "./pages/Membership";
import MentorSetup from "./pages/MentorSetup";
import MentorSetupFlow from "./pages/MentorSetupFlow";
import Mentors from "./pages/Mentors";
import MentorOnboarding from "./pages/MentorOnboarding";
import MentorDashboard from "./pages/MentorDashboard";
import MentorMagicPage from "./pages/MentorMagicPage";
import OrganizationDashboard from "./pages/OrganizationDashboard";
import OrganizationRenewal from "./pages/OrganizationRenewal";
import ReimagineArtwork from "./pages/ReimagineArtwork";
import ReimagineStudio from "./pages/ReimagineStudio";
import ArtAppreciationFeedback from './pages/ArtAppreciationFeedback';
import MigrationPage from './pages/MigrationPage';
import KudosTestPage from './pages/KudosTestPage'; // ✅ Kudos Test Route
import OnceUponAClick from './pages/OnceUponAClick';
import SelectArtworkForStory from './pages/SelectArtworkForStory';
import StoryboardingLanding from './pages/StoryboardingLanding';
import StoryboardingStudio from './pages/StoryboardingStudio';
import CharacterLibrary from './pages/CharacterLibrary';
import ArtWorldLandingPage from './pages/ArtWorldLandingPage';
import ArtStyleSelectionPage from './pages/ArtStyleSelectionPage';
import ActivityDashboard from './pages/ActivityDashboard';
import MentorActivityDashboard from './pages/MentorActivityDashboard';
import DailyArtActivity from './pages/DailyArtActivity';
import AdminActivityManager from './pages/AdminActivityManager';
import ActivityJourneyDisplay from './components/ActivityJourneyDisplay';
import PDFActivityDisplay from './pages/PDFActivityDisplay';
import SimplePDFUploader from './components/SimplePDFUploader';
import ActivityUploadPage from './pages/ActivityUploadPage';
import DailyActivitiesDashboard from './pages/DailyActivitiesDashboard';
import ActivityPage from './pages/ActivityPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmation from './pages/OrderConfirmation';
import AdminOrdersDashboard from './pages/AdminOrdersDashboard.jsx';
import DesignSystemDemo from './pages/DesignSystemDemo';
import BackgroundDemo from './pages/BackgroundDemo';
import BackgroundTest from './pages/BackgroundTest';
import SimpleBackgroundTest from './pages/SimpleBackgroundTest';
import BackgroundTestSimple from './pages/BackgroundTestSimple';
import MentorsCoLearning from './pages/MentorsCoLearning';
import AuthModalPage from './components/AuthModalPage';
import AdminArtworks from './pages/AdminArtworks';
import AdminVerifiedArtworksDashboard from './pages/AdminVerifiedArtworksDashboard';
import ArtHistoryCompare from './pages/ArtHistoryCompare';
import ArtHistoryAppreciation from './pages/ArtHistoryAppreciation';
import MyArtHistory from './pages/MyArtHistory';
import ArtMovementAdmin from './components/ArtMovementAdmin';
import AdminMembershipDashboard from './pages/AdminMembershipDashboard';
import StudioOnboardingPage from './pages/StudioOnboardingPage';
import ArtmoRedeemSuccessPage from './pages/ArtmoRedeemSuccessPage';
import MyArtWorldPage from './pages/MyArtWorldPage';
import DashboardLanding from './pages/DashboardLanding';
import WaysOfSeeingPage from './pages/WaysOfSeeingPage';
import LearnArtHistory from './pages/LearnArtHistory';
import LearnArtHistoryFlow from './pages/LearnArtHistoryFlow';

// Redirect /learn-art-history/portrait (or any :type) to main page with state so modal opens; avoids "coming soon" ever rendering
function RedirectLearnArtHistoryType() {
  const { type } = useParams();
  return <Navigate to="/learn-art-history" replace state={{ openModalForType: type || null }} />;
}

console.log('App.js rendered');

export default function App() {
  return (
    <div className="artm-app min-h-screen w-full">
    <AuthProvider>
      <QueueProvider>
        <AccountTypeRouter>
          <RouteTransition>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePageNew />} />
          <Route path="/home" element={<HomePageNew />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/parent-consent-approval" element={<ParentConsentApproval />} />
          {/* Handle Firebase action URL redirects */}
          <Route path="/__/auth/action" element={<FirebaseActionHandler />} />
          {/* Standard modal UI routes */}
          <Route path="/auth/login" element={<AuthModalPage />} />
          <Route path="/auth/signup" element={<AuthModalPage />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/onboarding/studio-access" element={
            <ProtectedRoute>
              <StudioOnboardingPage />
            </ProtectedRoute>
          } />
          <Route path="/onboarding/artmo-success" element={
            <ProtectedRoute>
              <ArtmoRedeemSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/mentors/setup" element={
            <ProtectedRoute>
              <MentorSetup />
            </ProtectedRoute>
          } />
          <Route path="/mentor-setup" element={
            <ProtectedRoute>
              <MentorSetupFlow />
            </ProtectedRoute>
          } />
          <Route path="/mentors" element={
            <ProtectedRoute>
              <Mentors />
            </ProtectedRoute>
          } />
          <Route path="/mentors/onboarding" element={<MentorOnboarding />} />
          <Route path="/mentor" element={
            <ProtectedRoute>
              <MentorRoute>
                <MentorDashboard />
              </MentorRoute>
            </ProtectedRoute>
          } />
          <Route path="/mentor-dashboard" element={
            <ProtectedRoute>
              <MentorRoute>
                <MentorDashboard />
              </MentorRoute>
            </ProtectedRoute>
          } />
          <Route path="/mentor-magic" element={
            <ProtectedRoute>
              <MentorRoute>
                <MentorMagicPage />
              </MentorRoute>
            </ProtectedRoute>
          } />
          <Route path="/mentors/dashboard" element={
            <ProtectedRoute>
              <MentorRoute>
                <MentorDashboard />
              </MentorRoute>
            </ProtectedRoute>
          } />
          <Route path="/organization-dashboard" element={
            <ProtectedRoute>
              <OrganizationDashboard />
            </ProtectedRoute>
          } />
          <Route path="/organization-renewal" element={
            <ProtectedRoute>
              <OrganizationRenewal />
            </ProtectedRoute>
          } />
          <Route path="/migrate" element={<MigrationPage />} />
          <Route path="/create-books" element={<CreateBooksPage />} />
          <Route path="/grow" element={<GrowPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/offerings" element={<OfferingsPage />} />
          {/* Gallery landing removed - use Learning Space or General Exhibit directly */}
          <Route path="/gallery/studio" element={
            <ProtectedRoute>
              <GalleryStudio />
            </ProtectedRoute>
          } />
          {/* General Exhibit should be publicly accessible (no membership/auth required) */}
          <Route path="/gallery/general" element={<GeneralExhibit />} />
          <Route path="/artists" element={<ArtistsProfiles />} />
          <Route path="/artist-public-gallery/:uid" element={<ArtistPublicGallery />} />
          
          {/* Protected Routes - Require Authentication */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ArtistProfile />
            </ProtectedRoute>
          } />
          <Route path="/artist-profile" element={
            <ProtectedRoute>
              <ArtistProfile />
            </ProtectedRoute>
          } />
          <Route path="/my-art-world" element={
            <ProtectedRoute>
              <DashboardLanding />
            </ProtectedRoute>
          } />
          <Route path="/artist-wall" element={
            <ProtectedRoute>
              <ArtistWall />
            </ProtectedRoute>
          } />
          <Route path="/activity-dashboard" element={
            <ProtectedRoute>
              <ActivityDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mentor-activity-dashboard" element={
            <MentorRoute>
              <MentorActivityDashboard />
            </MentorRoute>
          } />
          <Route path="/my-art-history" element={
            <ProtectedRoute>
              <MyArtHistory />
            </ProtectedRoute>
          } />
                  <Route path="/admin/activity-manager" element={
            <ProtectedRoute>
              <AdminActivityManager />
            </ProtectedRoute>
        } />
        <Route path="/upload-activity" element={
            <ProtectedRoute>
              <ActivityUploadPage />
            </ProtectedRoute>
        } />
        <Route path="/daily-activities" element={
            <ProtectedRoute>
              <DailyActivitiesDashboard />
            </ProtectedRoute>
        } />
        <Route path="/activity-journeys" element={
            <ProtectedRoute>
              <ActivityJourneyDisplay />
            </ProtectedRoute>
        } />
          <Route path="/magic" element={
            <ProtectedRoute>
              <AIMagicPage />
            </ProtectedRoute>
          } />
          <Route path="/story" element={
            <ProtectedRoute>
              <Story />
            </ProtectedRoute>
          } />
          <Route path="/story-creator/:id" element={
            <ProtectedRoute>
              <StoryCreator />
            </ProtectedRoute>
          } />
          <Route path="/story-creator" element={
            <ProtectedRoute>
              <StoryCreator />
            </ProtectedRoute>
          } />
          <Route path="/view-book/:id" element={
            <ProtectedRoute>
              <BookView />
            </ProtectedRoute>
          } />
          <Route path="/view-book" element={
            <ProtectedRoute>
              <BookView />
            </ProtectedRoute>
          } />
          <Route path="/edit-book/:id" element={
            <ProtectedRoute>
              <EditBookView />
            </ProtectedRoute>
          } />
          <Route path="/my-art-stories" element={
            <ProtectedRoute>
              <MyArtStories />
            </ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute>
              <AdminOrdersDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ways-of-seeing" element={
            <ProtectedRoute>
              <WaysOfSeeingPage />
            </ProtectedRoute>
          } />
          <Route path="/reimagine" element={
            <ProtectedRoute>
              <ReimagineArtwork />
            </ProtectedRoute>
          } />
          <Route path="/reimagine-studio" element={
            <ProtectedRoute>
              <ReimagineStudio />
            </ProtectedRoute>
          } />
          <Route path="/art-appreciation/feedback" element={
            <ProtectedRoute>
              <ArtAppreciationFeedback />
            </ProtectedRoute>
          } />
          <Route path="/storyboarding-landing" element={
            <ProtectedRoute>
              <StoryboardingLanding />
            </ProtectedRoute>
          } />
          <Route path="/storyboarding-studio" element={
            <ProtectedRoute>
              <StoryboardingStudio />
            </ProtectedRoute>
          } />
          <Route path="/character-library" element={
            <ProtectedRoute>
              <CharacterLibrary />
            </ProtectedRoute>
          } />
          <Route path="/once-upon-a-click" element={
            <ProtectedRoute>
              <OnceUponAClick />
            </ProtectedRoute>
          } />
          <Route path="/select-artwork-for-story" element={
            <ProtectedRoute>
              <SelectArtworkForStory />
            </ProtectedRoute>
          } />
          <Route path="/kudos-test" element={
            <ProtectedRoute>
              <KudosTestPage />
            </ProtectedRoute>
          } />
          <Route path="/daily-art-activity" element={
            <ProtectedRoute>
              <DailyArtActivity />
            </ProtectedRoute>
          } />
          <Route path="/pdf-activity-display" element={
            <ProtectedRoute>
              <PDFActivityDisplay />
            </ProtectedRoute>
          } />
          <Route path="/simple-pdf-uploader" element={
            <ProtectedRoute>
              <SimplePDFUploader />
            </ProtectedRoute>
          } />
          <Route path="/activity/:activityId" element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          } />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          
          {/* Art World Routes */}
          <Route path="/art-world" element={<ArtWorldLandingPage />} />
          <Route path="/art-world/selection" element={<ArtStyleSelectionPage />} />
          
          {/* Design System Demo */}
          <Route path="/design-demo" element={<DesignSystemDemo />} />
          <Route path="/background-demo" element={<BackgroundDemo />} />
          <Route path="/background-test" element={<BackgroundTest />} />
          <Route path="/simple-test" element={<SimpleBackgroundTest />} />
          <Route path="/test-simple" element={<BackgroundTestSimple />} />

          <Route path="/mentors/co-learning" element={<MentorsCoLearning />} />

          {/* Admin Routes */}
          <Route path="/admin-artworks" element={
            <ProtectedRoute>
              <AdminArtworks />
            </ProtectedRoute>
          } />
          <Route path="/admin/verified-artworks" element={
            <ProtectedRoute>
              <AdminVerifiedArtworksDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/art-movements" element={
            <ProtectedRoute>
              <ArtMovementAdmin />
            </ProtectedRoute>
          } />
          <Route path="/admin/membership-plans" element={
            <ProtectedRoute>
              <AdminMembershipDashboard />
            </ProtectedRoute>
          } />

          {/* Art History Routes */}
          <Route path="/learn-art-history" element={
            <ProtectedRoute>
              <LearnArtHistory />
            </ProtectedRoute>
          } />
          <Route path="/learn-art-history/flow" element={
            <ProtectedRoute>
              <LearnArtHistoryFlow />
            </ProtectedRoute>
          } />
          <Route path="/learn-art-history/:type" element={
            <ProtectedRoute>
              <RedirectLearnArtHistoryType />
            </ProtectedRoute>
          } />
          <Route path="/art-history" element={
            <ProtectedRoute>
              <ArtHistoryCompare />
            </ProtectedRoute>
          } />
          <Route path="/art-history-appreciation" element={
            <ProtectedRoute>
              <ArtHistoryAppreciation />
            </ProtectedRoute>
          } />
          <Route path="/art-appreciation" element={
            <ProtectedRoute>
              <ArtHistoryAppreciation />
            </ProtectedRoute>
          } />

          <Route path="*" element={<div style={{ color: 'red', padding: 40 }}>404: Route Not Found</div>} />
          </Routes>
          </RouteTransition>
        </AccountTypeRouter>
      </QueueProvider>
    </AuthProvider>
    </div>
  );
}
